// src/pages/SingleProduct.js
import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./styles/singleProduct.css";
import { IoIosHeartEmpty } from "react-icons/io";
import { PiShoppingCartSimpleLight } from "react-icons/pi";
import { TfiWrite } from "react-icons/tfi";
import { FaFacebookF, FaTwitter, FaLinkedinIn } from "react-icons/fa";
import CartAddBar from "../components/CartAddBar";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { pushRecentlyViewed, readRecentlyViewed } from "../utils/recentlyViewed";
import OfflineOverlay from "../components/OfflineOverlay";
import {
  getAProduct,
  resetSingleProduct,
  addToWishlists,
  addRating,
} from "../app/features/products/productSlice";
import { getAllColors } from "../app/features/color/colorSlice";
import { getAllSizes } from "../app/features/size/sizeSlice";
import { getPprices } from "../app/features/printPrice/printPriceSlice";
import VariantToast from "../components/VariantToast";
import {
  addProdToCart,
  getUserCart as fetchUserCart,
  updateCartProduct,
} from "../app/features/user/userSlice";

const GUEST_CART_KEY = "kupto_guest_cart_v1";

const buildGuestCartKey = (x) => {
  const pid = String(x?.productId || "");
  const size = x?.size ? String(x.size) : "";
  const color = x?.color ? String(x.color) : "";
  const side = x?.printSide ? String(x.printSide) : "";
  return `${pid}__${size}__${color}__${side}`;
};

const SingleProduct = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /* =========================
     ✅ OFFLINE LOGIC
  ========================== */
  const [isOffline, setIsOffline] = useState(() => {
  if (typeof window === "undefined") return false;
  return !navigator.onLine;
});
  const [productFetchFailed, setProductFetchFailed] = useState(false);

  // ✅ IMAGE LOADING
  const [loadedMap, setLoadedMap] = useState(() => ({}));
  const [errorMap, setErrorMap] = useState(() => ({}));

  const productSlug = params?.slug || "";

  const markLoaded = useCallback((url) => {
    if (!url) return;
    setLoadedMap((m) => ({ ...m, [url]: true }));
  }, []);

  const markError = useCallback((url) => {
    if (!url) return;
    setErrorMap((m) => ({ ...m, [url]: true }));
    setLoadedMap((m) => ({ ...m, [url]: true }));
  }, []);

  useEffect(() => {
    setLoadedMap({});
    setErrorMap({});
  }, [productSlug]);

  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const guardOffline = useCallback(
    (msg = "You are offline. Please connect to the internet and try again.") => {
      if (!isOffline) return false;
      console.warn(msg);
      return true;
    },
    [isOffline]
  );

  const productSlice = useSelector((state) => state.product || state.products || {});

  const singleProduct =
    productSlice?.singleProduct ||
    productSlice?.product ||
    productSlice?.aProduct ||
    null;

  const isLoading = Boolean(productSlice?.isLoading);

  const authState = useSelector((state) => state?.auth || {});
  const isLoggedIn = Boolean(authState?.user?.token || localStorage.getItem("token"));

  const cartState = useSelector((state) => state?.auth?.cartProduct);

  const cartList = useMemo(() => {
    if (Array.isArray(cartState)) return cartState;
    if (cartState && Array.isArray(cartState?.cart)) return cartState.cart;
    return [];
  }, [cartState]);

  const allColors = useSelector((state) => {
    const a = state?.color?.colors;
    const b = state?.colors?.colors;
    return (Array.isArray(a) ? a : null) ?? (Array.isArray(b) ? b : null) ?? [];
  });

  const allSizes = useSelector((state) => {
    const a = state?.size?.sizes;
    const b = state?.sizes?.sizes;
    return (Array.isArray(a) ? a : null) ?? (Array.isArray(b) ? b : null) ?? [];
  });

  const allProducts = useMemo(() => {
    return productSlice?.filteredProducts || productSlice?.products || [];
  }, [productSlice?.filteredProducts, productSlice?.products]);

  const printPriceState = useSelector(
    (state) => state?.printPrice || state?.printPrices || {}
  );

  const printPrices = useMemo(() => {
    const arr = printPriceState?.printPrices;
    return Array.isArray(arr) ? arr : [];
  }, [printPriceState?.printPrices]);

  const categoryNames = useMemo(() => {
    const p = singleProduct || {};
    const cats = Array.isArray(p?.category) ? p.category : [];

    const names = cats
      .map((c) => {
        if (!c) return "";
        if (typeof c === "object") {
          return c?.title || c?.name || c?.label || "";
        }
        return "";
      })
      .filter(Boolean);

    return Array.from(new Set(names));
  }, [singleProduct]);

  const categoryText = useMemo(() => {
    return categoryNames.length ? categoryNames.join(", ") : "—";
  }, [categoryNames]);

  const ImgWithLoader = useCallback(
    ({ src, alt, className = "", draggable = false, onClick }) => {
      const isLoaded = Boolean(loadedMap?.[src]);
      const isError = Boolean(errorMap?.[src]);

      return (
        <div className={`sp-imgLoaderWrap ${className}`} onClick={onClick}>
          {!isLoaded ? (
            <div className="sp-imgLoader" aria-label="Loading image">
              <AiOutlineLoading3Quarters className="sp-imgLoaderIcon" />
            </div>
          ) : null}

          {src && !isError ? (
            <img
              src={src}
              alt={alt}
              draggable={draggable}
              onLoad={() => markLoaded(src)}
              onError={() => markError(src)}
              style={{ opacity: isLoaded ? 1 : 0 }}
            />
          ) : (
            isLoaded && <div className="sp-emptyImg">Image failed to load</div>
          )}
        </div>
      );
    },
    [loadedMap, errorMap, markLoaded, markError]
  );

  useEffect(() => {
    if (isOffline) return;
    dispatch(getAllColors());
    dispatch(getAllSizes());
    dispatch(getPprices());
  }, [dispatch, isOffline]);

  useEffect(() => {
    if (isOffline) return;
    if (isLoggedIn) dispatch(fetchUserCart());
  }, [dispatch, isLoggedIn, isOffline]);

 
  useEffect(() => {
  if (!productSlug) return;

  if (isOffline) {
    setProductFetchFailed(true);
    return;
  }

  setProductFetchFailed(false);

  dispatch(getAProduct(productSlug))
    .unwrap()
    .then((res) => {
      const redirectTo = res?.redirectTo || res?.data?.redirectTo;

      if (redirectTo && redirectTo !== location.pathname) {
        navigate(redirectTo, { replace: true });
      }
    })
    .catch((err) => {
      console.error("Product fetch failed:", err);
      setProductFetchFailed(true);
    });

  return () => dispatch(resetSingleProduct());
}, [dispatch, productSlug, isOffline, navigate, location.pathname]);

  

  /* =====================================================
     ✅ RECENTLY VIEWED
  ====================================================== */
  useEffect(() => {
    if (!singleProduct?._id) return;
    try {
      const imgs =
        Array.isArray(singleProduct?.images) && singleProduct.images.length
          ? singleProduct.images
              .map((im) => (typeof im === "string" ? im : im?.url))
              .filter(Boolean)
          : [];

      pushRecentlyViewed({
        id: singleProduct._id,
        _id: singleProduct._id,
        slug: singleProduct.slug || "",
        title: singleProduct.title,
        images: imgs,
        img: imgs?.[0] || "",
        price: Number(singleProduct?.price ?? 0),
        totalrating: Number(singleProduct?.totalrating ?? 0),
      });
    } catch {
      // ignore
    }
  }, [
    singleProduct?._id,
    singleProduct?.slug,
    singleProduct?.title,
    singleProduct?.price,
    singleProduct?.images,
    singleProduct?.totalrating,
  ]);

  const [recentlyViewed, setRecentlyViewed] = useState(() => readRecentlyViewed());

  useEffect(() => {
    const refresh = () => setRecentlyViewed(readRecentlyViewed());

    window.addEventListener("recentlyViewedUpdated", refresh);

    const onStorage = (e) => {
      if (e?.key === "recentlyViewed") refresh();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("recentlyViewedUpdated", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  /* =========================
     ✅ GUEST CART
  ========================== */
  const readGuestCart = useCallback(() => {
    try {
      const raw = localStorage.getItem(GUEST_CART_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const writeGuestCart = useCallback((items) => {
    try {
      localStorage.setItem(
        GUEST_CART_KEY,
        JSON.stringify(Array.isArray(items) ? items : [])
      );
    } catch {
      // ignore
    }
  }, []);

  const [guestCart, setGuestCart] = useState(() => readGuestCart());

  useEffect(() => {
    setGuestCart(readGuestCart());
  }, [isLoggedIn, readGuestCart]);

  const addToGuestCart = useCallback(
    (payload) => {
      const list = readGuestCart();
      const pid = String(payload?.productId || "");

      const idx = list.findIndex((it) => String(it?.productId || "") === pid);

      if (idx >= 0) {
        const prevQty = Number(list[idx]?.quantity || 0);
        const addQty = Number(payload?.quantity || 0);
        const nextQty = Math.max(1, prevQty + Math.max(1, addQty));

        list[idx] = {
          ...list[idx],
          ...payload,
          quantity: nextQty,
        };
      } else {
        list.push({
          ...payload,
          addedAt: new Date().toISOString(),
        });
      }

      writeGuestCart(list);
      setGuestCart(list);

      window.dispatchEvent(new Event("guestCartUpdated"));
    },
    [readGuestCart, writeGuestCart]
  );

  const [cartNotice, setCartNotice] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const showCartNotice = useCallback((message, type = "success") => {
    setCartNotice({ open: true, type, message });

    window.clearTimeout(window.__kuptoCartNoticeTimer);
    window.__kuptoCartNoticeTimer = window.setTimeout(() => {
      setCartNotice((prev) => ({ ...prev, open: false }));
    }, 2500);
  }, []);

  useEffect(() => {
    return () => {
      window.clearTimeout(window.__kuptoCartNoticeTimer);
    };
  }, []);

  /* =========================
     HELPERS
  ========================== */
  const stripHtmlTags = (html = "") =>
    String(html).replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

  const formatUGX = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num)) return "UGX 0";
    return `UGX ${num.toLocaleString()}`;
  };

  /* =========================
     ✅ MAP SIZES / COLORS
  ========================== */
  const sizeById = useMemo(() => {
    const m = new Map();
    (allSizes || []).forEach((s) => {
      if (s?._id) m.set(String(s._id), s);
    });
    return m;
  }, [allSizes]);

  const getSizeName = useCallback((s) => {
    if (!s) return "";
    const name =
      s?.name ||
      s?.title ||
      s?.labelName ||
      s?.sizeName ||
      s?.displayName ||
      "";
    if (String(name).trim()) return String(name).trim();
    const fallback = s?.label || s?.size || s?.value || "";
    return String(fallback || "").trim();
  }, []);

  const getSizeLabel = useCallback((s) => {
    if (!s) return "";
    return String(s.label || s.title || s.name || s.size || "").trim();
  }, []);

  const getSizeNameWithMM = useCallback(
    (sizeRaw) => {
      if (!sizeRaw) return "";
      const name = getSizeName(sizeRaw) || "";
      if (/\b(mm|cm|in|inch|inches)\b/i.test(name)) return name;

      const w = Number(
        sizeRaw?.width ??
          sizeRaw?.w ??
          sizeRaw?.mmWidth ??
          sizeRaw?.widthMm ??
          NaN
      );
      const h = Number(
        sizeRaw?.height ??
          sizeRaw?.h ??
          sizeRaw?.mmHeight ??
          sizeRaw?.heightMm ??
          NaN
      );
      const unit = String(sizeRaw?.unit || "mm").trim() || "mm";

      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        return name ? `${name} (${w}×${h} ${unit})` : `${w}×${h} ${unit}`;
      }

      const A_MM = {
        A0: "841×1189 mm",
        A1: "594×841 mm",
        A2: "420×594 mm",
        A3: "297×420 mm",
        A4: "210×297 mm",
        A5: "148×210 mm",
        A6: "105×148 mm",
        A7: "74×105 mm",
      };

      const matchA = name.match(/\bA[0-7]\b/i);
      if (matchA) {
        const key = matchA[0].toUpperCase();
        if (A_MM[key]) return `${name} (${A_MM[key]})`;
      }

      return name;
    },
    [getSizeName]
  );

  /* =====================================================
     ✅ PRINT PRICING RESOLVER
  ====================================================== */
  const resolvePrintPricing = useCallback(
    (p) => {
      if (!p) return null;

      const directObj =
        p?.printingPrice &&
        typeof p.printingPrice === "object" &&
        (p.printingPrice?.title || p.printingPrice?.printPrice)
          ? p.printingPrice
          : p?.printPricing &&
            typeof p.printPricing === "object" &&
            (p.printPricing?.title || p.printPricing?.printPrice)
          ? p.printPricing
          : p?.printPrice &&
            typeof p.printPrice === "object" &&
            (p.printPrice?.title || p.printPrice?.printPrice)
          ? p.printPrice
          : null;

      const idCandidates = [
        typeof p?.printingPrice === "string" ? p.printingPrice : null,
        typeof p?.printingPriceId === "string" ? p.printingPriceId : null,
        typeof p?.printPricingId === "string" ? p.printPricingId : null,
        typeof p?.printPrice === "string" ? p.printPrice : null,
        typeof p?.printPriceId === "string" ? p.printPriceId : null,
        typeof p?.pprice === "string" ? p.pprice : null,
        typeof p?.ppriceId === "string" ? p.ppriceId : null,
      ].filter(Boolean);

      const titleCandidates = [
        p?.printingType,
        p?.printType,
        p?.printPriceTitle,
        p?.printPricingTitle,
        p?.printPriceName,
        p?.printingPriceTitle,
      ]
        .map((x) => (x == null ? "" : String(x)))
        .map((x) => x.trim())
        .filter(Boolean);

      const byId = (id) => {
        if (!id) return null;
        return (
          (printPrices || []).find((x) => String(x?._id) === String(id)) || null
        );
      };

      const byTitle = (t) => {
        const s = String(t || "").trim().toLowerCase();
        if (!s) return null;
        return (
          (printPrices || []).find(
            (x) => String(x?.title || "").trim().toLowerCase() === s
          ) || null
        );
      };

      let record = directObj;

      if (!record) {
        for (const id of idCandidates) {
          record = byId(id);
          if (record) break;
        }
      }

      if (!record) {
        for (const t of titleCandidates) {
          record = byTitle(t);
          if (record) break;
        }
      }

      if (!record) return null;

      const preparePrice = Number(record?.preparePrice ?? 0) || 0;
      const oneSide = Number(record?.printPrice?.oneSide ?? 0) || 0;
      const twoSide = Number(record?.printPrice?.twoSide ?? 0) || 0;
      const discOne = Number(record?.printPriceDiscount?.oneSide ?? 0) || 0;
      const discTwo = Number(record?.printPriceDiscount?.twoSide ?? 0) || 0;

      const discountMinQty =
        record?.discountMinQty !== undefined && record?.discountMinQty !== null
          ? Number(record.discountMinQty)
          : null;

      return {
        id: record?._id || null,
        title: record?.title || "",
        preparePrice,
        discountMinQty: Number.isFinite(discountMinQty) ? discountMinQty : null,
        printPrice: { oneSide, twoSide },
        printPriceDiscount: { oneSide: discOne, twoSide: discTwo },
      };
    },
    [printPrices]
  );

  /* =========================
     ✅ NORMALIZE PRODUCT
  ========================== */
  const normalized = useMemo(() => {
    const p = singleProduct || {};

    const imgs =
      Array.isArray(p?.images) && p.images.length
        ? p.images
            .map((im) => (typeof im === "string" ? im : im?.url))
            .filter(Boolean)
        : [];

    const variantMap = new Map(
      (Array.isArray(p?.variantImages) ? p.variantImages : []).map((v) => {
        const colorId = String(v?.color?._id || v?.color || "");
        const urls = (v?.images || []).map((im) => im?.url).filter(Boolean);
        return [colorId, urls];
      })
    );

    const rawColors = p?.color ?? p?.colors ?? [];
    const colorArr = Array.isArray(rawColors)
      ? rawColors
      : rawColors
      ? [rawColors]
      : [];

    const colors = colorArr
      .map((c) => {
        const fromStore =
          typeof c === "string"
            ? allColors.find((x) => String(x?._id) === String(c)) ||
              allColors.find(
                (x) =>
                  String(x?.name || "").toLowerCase() ===
                  String(c).toLowerCase()
              ) ||
              null
            : c;

        const cid = String(fromStore?._id || c || "");
        const label =
          fromStore?.name ||
          fromStore?.title ||
          fromStore?.color ||
          (typeof c === "string" ? c : "Color");

        const hex = fromStore?.hex || fromStore?.code || fromStore?.value || null;

        const variantImgs = variantMap.get(cid) || [];
        const swatchUrl =
          fromStore?.swatchImage?.url ||
          fromStore?.image?.url ||
          fromStore?.imageUrl ||
          "";

        return {
          id: fromStore?._id || (typeof c === "string" ? c : null),
          label,
          hex,
          imageUrl: variantImgs?.[0] || swatchUrl || "",
          allVariantImages: variantImgs,
        };
      })
      .filter((x) => x?.label);

    const rawSizes = p?.size ?? p?.sizes ?? [];
    const sizeArr = Array.isArray(rawSizes)
      ? rawSizes
      : rawSizes
      ? [rawSizes]
      : [];

    const sizes = sizeArr
      .map((s) => {
        if (typeof s === "string") {
          const fromStore = sizeById.get(String(s)) || null;
          const finalObj = fromStore || { name: s };

          const sizeName = getSizeName(finalObj) || s;
          const sizeNameWithMM = getSizeNameWithMM(finalObj) || sizeName;

          return {
            id: fromStore?._id ? String(fromStore._id) : s,
            name: sizeName,
            nameWithMM: sizeNameWithMM,
            raw: finalObj,
          };
        }

        const id = s?._id ? String(s._id) : null;
        const fromStore = id ? sizeById.get(id) : null;
        const finalObj = fromStore || s;

        const sizeName = getSizeName(finalObj) || getSizeLabel(finalObj) || "";
        const sizeNameWithMM = getSizeNameWithMM(finalObj) || sizeName;

        return {
          id: id || null,
          name: sizeName,
          nameWithMM: sizeNameWithMM,
          raw: finalObj,
        };
      })
      .filter((x) => x?.name)
      .filter((x, idx, arr) => {
        const key = x.id ? `id:${x.id}` : `name:${x.name}`;
        return (
          idx ===
          arr.findIndex((y) => (y.id ? `id:${y.id}` : `name:${y.name}`) === key)
        );
      });

    const ratingsArr = Array.isArray(p?.ratings) ? p.ratings : [];
    const reviewsCount = ratingsArr.length || 0;
    const avgRating =
      Number(p?.totalrating) ||
      (reviewsCount
        ? ratingsArr.reduce((sum, r) => sum + (Number(r?.star) || 0), 0) /
          reviewsCount
        : 0);

    const tags = Array.isArray(p?.tags) ? p.tags.filter(Boolean) : [];
    const tagSet = new Set(tags.map((t) => String(t).toLowerCase().trim()));
    const hasFileUploadTag =
      tagSet.has("file upload") || tagSet.has("featured file upload");

    const printSpec = {
      allowedExtensions:
        p?.printSpec?.allowedExtensions ?? ["pdf", "png", "jpg", "jpeg", "cdr", "ai"],
      maxFileSizeMB: Number(p?.printSpec?.maxFileSizeMB ?? 10),
      instructions: String(p?.printSpec?.instructions ?? ""),
    };

    const bulkMinRaw =
      p?.bulkDiscount?.minQty ??
      p?.bulkDiscountMinQty ??
      p?.bulkMinQty ??
      p?.bulkDiscountQty ??
      null;

    const bulkPriceRaw =
      p?.bulkDiscount?.price ??
      p?.bulkDiscountPrice ??
      p?.bulkPrice ??
      p?.bulkDiscountUnitPrice ??
      null;

    const bulkMin =
      bulkMinRaw !== "" && bulkMinRaw !== null && bulkMinRaw !== undefined
        ? Number(bulkMinRaw)
        : null;

    const bulkPrice =
      bulkPriceRaw !== "" &&
      bulkPriceRaw !== null &&
      bulkPriceRaw !== undefined
        ? Number(bulkPriceRaw)
        : null;

    const moqRaw =
      p?.minOrder ??
      p?.minOrderQty ??
      p?.minQty ??
      p?.minimumOrderQty ??
      p?.minimumQty ??
      p?.moq ??
      p?.minOrderQuantity ??
      null;

    const moq =
      moqRaw !== "" && moqRaw !== null && moqRaw !== undefined
        ? Number(moqRaw)
        : null;

    const printPricing = resolvePrintPricing(p);

    return {
      id: p?._id || "",
      slug: p?.slug || "",
      title: p?.title || "Product",
      brand: p?.brand || "",
      description: p?.description || "",
      price: Number(p?.price ?? 0),

      discountedPrice:
        p?.discountedPrice !== "" &&
        p?.discountedPrice !== null &&
        p?.discountedPrice !== undefined
          ? Number(p.discountedPrice)
          : null,

      bulkDiscountMinQty: Number.isFinite(bulkMin) ? bulkMin : null,
      bulkDiscountPrice: Number.isFinite(bulkPrice) ? bulkPrice : null,
      minOrderQty: Number.isFinite(moq) && moq >= 1 ? moq : null,

      quantity: Number(p?.quantity ?? 0),
      tags,
      images: imgs,
      colors,
      sizes,
      ratingsArr,
      avgRating: Number(avgRating || 0),
      reviewsCount,
      hasFileUploadTag,
      isPrintable: Boolean(p?.isPrintable) || hasFileUploadTag,
      printSpec,
      printPricing,
      raw: p,
    };
  }, [
    singleProduct,
    allColors,
    sizeById,
    getSizeName,
    getSizeLabel,
    getSizeNameWithMM,
    resolvePrintPricing,
  ]);

  /* =========================
     ✅ APP MODE
  ========================== */
  const [isApp, setIsApp] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 560px)");
    const apply = () => setIsApp(Boolean(mq.matches));
    apply();

    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener(apply);
    };
  }, []);

  /* =========================
     ✅ PRODUCT PRICE LOGIC
  ========================== */
  const getColorPriceByQtyDisplay = useCallback(
    (colorId, qtyNum = 1) => {
      const q = Math.max(1, Number(qtyNum || 1));
      const p = singleProduct || {};

      const basePrice = Number(p?.price ?? 0);

      const productDiscountedPrice =
        p?.discountedPrice !== null &&
        p?.discountedPrice !== undefined &&
        p?.discountedPrice !== ""
          ? Number(p.discountedPrice)
          : null;

      const productDiscountMinQty =
        p?.discountMinQty !== null &&
        p?.discountMinQty !== undefined &&
        p?.discountMinQty !== ""
          ? Number(p.discountMinQty)
          : null;

      const productBulkMinQty =
        p?.bulkDiscount?.minQty !== null &&
        p?.bulkDiscount?.minQty !== undefined &&
        p?.bulkDiscount?.minQty !== ""
          ? Number(p.bulkDiscount.minQty)
          : null;

      const productBulkPrice =
        p?.bulkDiscount?.price !== null &&
        p?.bulkDiscount?.price !== undefined &&
        p?.bulkDiscount?.price !== ""
          ? Number(p.bulkDiscount.price)
          : null;

      const variants = Array.isArray(p?.colorVariants) ? p.colorVariants : [];
      const variant =
        colorId != null
          ? variants.find((v) => String(v?.color?._id || v?.color) === String(colorId))
          : null;

      const finalBasePrice =
        variant?.price !== null && variant?.price !== undefined && variant?.price !== ""
          ? Number(variant.price)
          : basePrice;

      const finalDiscountedPrice =
        variant?.discountedPrice !== null &&
        variant?.discountedPrice !== undefined &&
        variant?.discountedPrice !== ""
          ? Number(variant.discountedPrice)
          : productDiscountedPrice !== null && productDiscountedPrice < finalBasePrice
          ? productDiscountedPrice
          : null;

      const finalDiscountMinQty =
        finalDiscountedPrice !== null
          ? variant?.discountMinQty !== null &&
            variant?.discountMinQty !== undefined &&
            variant?.discountMinQty !== ""
            ? Number(variant.discountMinQty)
            : productDiscountMinQty
          : null;

      const finalBulkPrice =
        variant?.bulkDiscount?.price !== null &&
        variant?.bulkDiscount?.price !== undefined &&
        variant?.bulkDiscount?.price !== ""
          ? Number(variant.bulkDiscount.price)
          : productBulkPrice !== null && productBulkPrice <= finalBasePrice
          ? productBulkPrice
          : null;

      const finalBulkMinQty =
        finalBulkPrice !== null
          ? variant?.bulkDiscount?.minQty !== null &&
            variant?.bulkDiscount?.minQty !== undefined &&
            variant?.bulkDiscount?.minQty !== ""
            ? Number(variant.bulkDiscount.minQty)
            : productBulkMinQty
          : null;

      if (
        finalBulkMinQty !== null &&
        Number.isFinite(finalBulkMinQty) &&
        finalBulkPrice !== null &&
        Number.isFinite(finalBulkPrice) &&
        q >= finalBulkMinQty
      ) {
        return {
          current: finalBulkPrice,
          old: finalBasePrice > finalBulkPrice ? finalBasePrice : null,
          source: "color.bulkDiscount",
          isBulkDiscount: true,
          hasOwnPrice: Boolean(variant),
          variant,
        };
      }

      if (
        finalDiscountedPrice !== null &&
        Number.isFinite(finalDiscountedPrice) &&
        finalDiscountMinQty !== null &&
        Number.isFinite(finalDiscountMinQty) &&
        q >= finalDiscountMinQty
      ) {
        return {
          current: finalDiscountedPrice,
          old: finalBasePrice > finalDiscountedPrice ? finalBasePrice : null,
          source: "color.discount",
          isBulkDiscount: false,
          hasOwnPrice: Boolean(variant),
          variant,
        };
      }

      return {
        current: finalBasePrice,
        old: null,
        source: variant?.price != null ? "color.price" : "product.base",
        isBulkDiscount: false,
        hasOwnPrice: Boolean(variant?.price != null),
        variant,
      };
    },
    [singleProduct]
  );

  const getSizePriceByQtyDisplay = useCallback((sizeObj, qtyNum = 1, basePrice = 0) => {
    const base = Number(basePrice || 0);
    const q = Math.max(1, Number(qtyNum || 1));

    if (!sizeObj) {
      return {
        current: base,
        old: null,
        source: "base",
        isBulkDiscount: false,
        hasOwnPrice: false,
      };
    }

    const priceRaw = sizeObj?.price;
    const adjRaw = sizeObj?.priceAdjustment;
    const priceNum = Number(priceRaw);

    const hasOwnPrice =
      priceRaw !== null &&
      priceRaw !== undefined &&
      String(priceRaw).trim() !== "" &&
      Number.isFinite(priceNum) &&
      priceNum > 0;

    const adjNum = Number(adjRaw || 0);

    const baseCurrent = hasOwnPrice
      ? priceNum
      : base + (Number.isFinite(adjNum) ? adjNum : 0);

    const discountPriceRaw = sizeObj?.discountPrice;
    const discountMinQtyRaw = sizeObj?.discountMinQty;
    const discountPriceNum = Number(discountPriceRaw);
    const discountMinQtyNum = Number(discountMinQtyRaw);

    const hasDiscount =
      discountPriceRaw !== null &&
      discountPriceRaw !== undefined &&
      String(discountPriceRaw).trim() !== "" &&
      Number.isFinite(discountPriceNum);

    const hasMinQty =
      discountMinQtyRaw !== null &&
      discountMinQtyRaw !== undefined &&
      String(discountMinQtyRaw).trim() !== "" &&
      Number.isFinite(discountMinQtyNum);

    const bulkApplies = hasDiscount && hasMinQty && q >= discountMinQtyNum;

    if (bulkApplies) {
      const bulk = discountPriceNum;
      const old = baseCurrent > bulk ? baseCurrent : null;
      return {
        current: bulk,
        old,
        source: "size.bulkDiscount",
        isBulkDiscount: true,
        hasOwnPrice,
      };
    }

    return {
      current: baseCurrent,
      old: null,
      source: hasOwnPrice ? "size.price" : "size.adjustment",
      isBulkDiscount: false,
      hasOwnPrice,
    };
  }, []);

  const getProductPriceByQtyDisplay = useCallback(
    (qtyNum = 1, baseCurrent = 0, baseOld = null) => {
      const q = Math.max(1, Number(qtyNum || 1));
      const min = Number(normalized.bulkDiscountMinQty);
      const disc = Number(normalized.bulkDiscountPrice);

      const hasBulk =
        Number.isFinite(min) && min >= 1 && Number.isFinite(disc) && disc >= 0;

      if (hasBulk && q >= min) {
        const old = Number(baseCurrent) > disc ? Number(baseCurrent) : null;
        return {
          current: disc,
          old: old || baseOld || null,
          source: "product.bulkDiscount",
          isBulkDiscount: true,
        };
      }

      return {
        current: baseCurrent,
        old: baseOld || null,
        source: "product.base",
        isBulkDiscount: false,
      };
    },
    [normalized.bulkDiscountMinQty, normalized.bulkDiscountPrice]
  );

  /* =========================
     UI STATE
  ========================== */
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [printSide, setPrintSide] = useState("");

  const minOrderQty = useMemo(
    () => Number(normalized.minOrderQty || 1),
    [normalized.minOrderQty]
  );

  const [qty, setQty] = useState(1);

  const basePriceDisplay = useMemo(() => {
    return getColorPriceByQtyDisplay(selectedColorId, qty);
  }, [getColorPriceByQtyDisplay, selectedColorId, qty]);

  useEffect(() => {
    const moq = Number(normalized?.minOrderQty || 1);
    const safe = Number.isFinite(moq) && moq >= 1 ? moq : 1;
    setQty((qv) => Math.max(safe, Number(qv || 1)));
  }, [normalized?.minOrderQty, normalized?.id]);

  useEffect(() => {
    setPrintSide("");
  }, [normalized?.id]);

  const gallery = useMemo(() => normalized.images || [], [normalized.images]);
  const firstGalleryImg = useMemo(() => gallery?.[0] || "", [gallery]);

  const [activeImg, setActiveImg] = useState("");

  useEffect(() => {
    setActiveImg(firstGalleryImg);
  }, [firstGalleryImg]);

  const selectedColorVariant = useMemo(() => {
    if (!selectedColorId) return null;
    const variants = Array.isArray(singleProduct?.colorVariants)
      ? singleProduct.colorVariants
      : [];

    return (
      variants.find((v) => String(v?.color?._id || v?.color) === String(selectedColorId)) ||
      null
    );
  }, [singleProduct, selectedColorId]);

  const selectedSizeObj = useMemo(() => {
    if (!selectedSizeId) return null;
    return (
      (normalized.sizes || []).find((s) => String(s.id) === String(selectedSizeId))?.raw ||
      null
    );
  }, [selectedSizeId, normalized.sizes]);

  const selectedSizeNameWithMM = useMemo(() => {
    if (!selectedSizeId) return "";
    const hit = (normalized.sizes || []).find(
      (s) => String(s.id) === String(selectedSizeId)
    );
    return hit?.nameWithMM || hit?.name || "";
  }, [selectedSizeId, normalized.sizes]);

  const finalPriceDisplay = useMemo(() => {
    if (selectedSizeObj) {
      const sizePrice = getSizePriceByQtyDisplay(
        selectedSizeObj,
        qty,
        basePriceDisplay.current
      );

      if (sizePrice.hasOwnPrice || sizePrice.isBulkDiscount) {
        return sizePrice;
      }

      return {
        current: sizePrice.current,
        old: basePriceDisplay.old || null,
        source: `${basePriceDisplay.source}+size.adjustment`,
        isBulkDiscount: basePriceDisplay.isBulkDiscount,
      };
    }

    return basePriceDisplay;
  }, [selectedSizeObj, qty, getSizePriceByQtyDisplay, basePriceDisplay]);

  /* =====================================================
     ✅ PRINT PRICE LOGIC
  ====================================================== */
  const printPricingEnabled = Boolean(normalized?.printPricing?.title);

  const printDiscountApplies = useMemo(() => {
    if (!printPricingEnabled) return false;

    const min = Number(normalized?.printPricing?.discountMinQty);
    if (!Number.isFinite(min) || min < 1) return false;

    return Number(qty || 1) >= min;
  }, [printPricingEnabled, normalized?.printPricing?.discountMinQty, qty]);

  const printUnitPrice = useMemo(() => {
    if (!printPricingEnabled) return 0;
    const side = printSide || "";
    if (!side) return 0;

    const base = Number(normalized?.printPricing?.printPrice?.[side] ?? 0) || 0;
    const disc = Number(normalized?.printPricing?.printPriceDiscount?.[side] ?? 0) || 0;

    if (printDiscountApplies && Number.isFinite(disc) && disc > 0) return disc;
    return base;
  }, [printPricingEnabled, normalized?.printPricing, printSide, printDiscountApplies]);

  const printPreparePrice = useMemo(() => {
    if (!printPricingEnabled) return 0;
    return Number(normalized?.printPricing?.preparePrice ?? 0) || 0;
  }, [printPricingEnabled, normalized?.printPricing]);

  /* =========================
     ✅ IS IN CART?
  ========================== */
  const activeCartList = useMemo(
    () => (isLoggedIn ? cartList || [] : guestCart || []),
    [isLoggedIn, cartList, guestCart]
  );

  const isInCart = useMemo(() => {
    const pid = String(normalized?.id || "");
    if (!pid) return false;

    return (activeCartList || []).some((it) => {
      const itemPid =
        it?.productId ||
        it?.productId?._id ||
        it?.product?._id ||
        it?.product ||
        it?._id;

      const realPid = typeof itemPid === "object" ? itemPid?._id : itemPid;
      return realPid ? String(realPid) === pid : false;
    });
  }, [activeCartList, normalized?.id]);

  const currentCartItem = useMemo(() => {
    const pid = String(normalized?.id || "");
    if (!pid) return null;

    const selectedColorKey = selectedColorId ? String(selectedColorId) : "";
    const selectedSizeKey = selectedSizeId ? String(selectedSizeId) : "";
    const selectedSideKey = printSide ? String(printSide) : "";
    const list = Array.isArray(activeCartList) ? activeCartList : [];

    const exact = list.find((it) => {
      const itemPid =
        it?.productId?._id ||
        it?.product?._id ||
        it?.productId ||
        it?.product ||
        it?._id ||
        "";

      if (String(itemPid) !== pid) return false;

      const itemColor = String(it?.color?._id || it?.color || "");
      const itemSize = String(it?.size?._id || it?.size || "");
      const itemSide = String(it?.printSide || "");

      const colorOk = selectedColorKey ? itemColor === selectedColorKey : true;
      const sizeOk = selectedSizeKey ? itemSize === selectedSizeKey : true;
      const sideOk = selectedSideKey ? itemSide === selectedSideKey : true;

      return colorOk && sizeOk && sideOk;
    });

    if (exact) return exact;

    return (
      list.find((it) => {
        const itemPid =
          it?.productId?._id ||
          it?.product?._id ||
          it?.productId ||
          it?.product ||
          it?._id ||
          "";
        return String(itemPid) === pid;
      }) || null
    );
  }, [activeCartList, normalized?.id, selectedColorId, selectedSizeId, printSide]);

  const syncQtyToExistingCart = useCallback(
    async (nextQty) => {
      const safeQty = Math.max(minOrderQty, Math.max(1, Number(nextQty || 1)));
      setQty(safeQty);

      if (!isInCart || !currentCartItem) return;

      try {
        if (isLoggedIn) {
          const cartItemId = currentCartItem?._id || currentCartItem?.cartItemId;
          if (!cartItemId) return;

          await dispatch(
            updateCartProduct({
              cartItemId,
              quantity: safeQty,
            })
          ).unwrap();

          dispatch(fetchUserCart());
          showCartNotice("Item quantity has been updated", "info");
          return;
        }

        const list = readGuestCart();
        const currentKey = buildGuestCartKey(currentCartItem);

        const next = list.map((it) => {
          const key = it?.key || buildGuestCartKey(it);
          if (String(key) !== String(currentKey)) return it;
          return {
            ...it,
            key,
            quantity: safeQty,
          };
        });

        writeGuestCart(next);
        setGuestCart(next);
        showCartNotice("Item quantity has been updated", "info");
      } catch (err) {
        console.error("Failed to sync qty from product page:", err);
      }
    },
    [
      currentCartItem,
      dispatch,
      isInCart,
      isLoggedIn,
      minOrderQty,
      readGuestCart,
      writeGuestCart,
      showCartNotice,
    ]
  );

  /* =====================================================
     ✅ PREPARE PRICE "ONCE" LOGIC
  ====================================================== */
  const printPricingEnabledRef = useRef(false);

  useEffect(() => {
    printPricingEnabledRef.current = Boolean(printPricingEnabled);
  }, [printPricingEnabled]);

  const getPrintKey = useCallback(() => {
    if (!printPricingEnabled) return "";
    const recId = normalized?.printPricing?.id ? String(normalized.printPricing.id) : "";
    const recTitle = normalized?.printPricing?.title
      ? String(normalized.printPricing.title)
      : "";

    return recId
      ? `pprice:${recId}`
      : recTitle
      ? `ppriceTitle:${recTitle.toLowerCase()}`
      : "";
  }, [printPricingEnabled, normalized?.printPricing?.id, normalized?.printPricing?.title]);

  const hasPrepareAlreadyApplied = useCallback(
    (printKey) => {
      if (!printKey) return false;
      const list = Array.isArray(activeCartList) ? activeCartList : [];

      return list.some((it) => {
        const key = String(it?.printKey || it?.printPriceKey || it?.printPricingKey || "");
        const fee =
          Number(it?.preparePriceOnce ?? it?.preparePrice ?? it?.setupFee ?? 0) || 0;
        const applied = Boolean(it?.preparePriceApplied || it?.setupFeeApplied);
        return key && key === printKey && (applied || fee > 0);
      });
    },
    [activeCartList]
  );

  /* =========================
     ✅ APP SWIPE CAROUSEL
  ========================== */
  const [activeIndex, setActiveIndex] = useState(0);
  const [appOverrideImg, setAppOverrideImg] = useState("");
  const appTrackRef = useRef(null);

  const scrollToIndex = useCallback((idx) => {
    const track = appTrackRef.current;
    if (!track) return;

    const slides = track.querySelectorAll(".sp-appSlide");
    const el = slides?.[idx];
    if (!el) return;

    const left = el.offsetLeft;
    track.scrollTo({ left, behavior: "smooth" });
  }, []);

  const rafRef = useRef(null);

  const onAppScroll = useCallback(() => {
    const track = appTrackRef.current;
    if (!track) return;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      const slides = track.querySelectorAll(".sp-appSlide");
      if (!slides?.length) return;

      const left = track.scrollLeft;
      let bestIdx = 0;
      let bestDist = Infinity;

      slides.forEach((el, i) => {
        const dist = Math.abs(el.offsetLeft - left);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      });

      setActiveIndex(bestIdx);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    setActiveIndex(0);
    setAppOverrideImg("");
    requestAnimationFrame(() => scrollToIndex(0));
  }, [normalized?.id, scrollToIndex]);

  const clampIndex = useCallback(
    (idx) => {
      const len = (gallery || []).length;
      if (!len) return 0;
      const next = ((idx % len) + len) % len;
      return next;
    },
    [gallery]
  );

  const goNext = useCallback(() => {
    const next = clampIndex(activeIndex + 1);
    setActiveIndex(next);
    setAppOverrideImg("");
    scrollToIndex(next);
  }, [activeIndex, clampIndex, scrollToIndex]);

  const goPrev = useCallback(() => {
    const prev = clampIndex(activeIndex - 1);
    setActiveIndex(prev);
    setAppOverrideImg("");
    scrollToIndex(prev);
  }, [activeIndex, clampIndex, scrollToIndex]);

  const autoTimerRef = useRef(null);

  const resetAutoTimer = useCallback(() => {
  if (autoTimerRef.current) clearInterval(autoTimerRef.current);

  if (appOverrideImg) return;

  if (!isApp) return;
  if ((gallery || []).length < 2) return;

  autoTimerRef.current = setInterval(() => {
    setActiveIndex((i) => {
      const next = clampIndex(i + 1);
      requestAnimationFrame(() => scrollToIndex(next));
      return next;
    });
    setAppOverrideImg("");
  }, 5000);
}, [isApp, gallery, clampIndex, scrollToIndex, appOverrideImg]); // ✅ added here

  useEffect(() => {
    resetAutoTimer();
    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    };
  }, [resetAutoTimer]);

  const touchRef = useRef({ startX: 0, lastX: 0, dragging: false });

  const onTouchStart = useCallback(
    (e) => {
      if (!isApp) return;
      const t = e.touches?.[0];
      if (!t) return;

      touchRef.current.startX = t.clientX;
      touchRef.current.lastX = t.clientX;
      touchRef.current.dragging = true;
      resetAutoTimer();
    },
    [isApp, resetAutoTimer]
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!isApp) return;
      const t = e.touches?.[0];
      if (!t) return;
      touchRef.current.lastX = t.clientX;
    },
    [isApp]
  );

  const onTouchEnd = useCallback(() => {
    if (!isApp) return;
    if (!touchRef.current.dragging) return;

    const dx = touchRef.current.lastX - touchRef.current.startX;
    touchRef.current.dragging = false;

    const THRESH = 45;
    if (dx <= -THRESH) goNext();
    else if (dx >= THRESH) goPrev();

    resetAutoTimer();
  }, [isApp, goNext, goPrev, resetAutoTimer]);

  const pointerRef = useRef({
    down: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    moved: false,
    pointerId: null,
  });

  const onPointerDown = useCallback(
    (e) => {
      if (!isApp) return;
      if (e.pointerType !== "touch" && e.pointerType !== "pen") return;

      pointerRef.current.down = true;
      pointerRef.current.moved = false;
      pointerRef.current.startX = e.clientX;
      pointerRef.current.startY = e.clientY;
      pointerRef.current.lastX = e.clientX;
      pointerRef.current.lastY = e.clientY;
      pointerRef.current.pointerId = e.pointerId;

      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }

      resetAutoTimer();
    },
    [isApp, resetAutoTimer]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!isApp) return;
      if (!pointerRef.current.down) return;
      if (pointerRef.current.pointerId !== e.pointerId) return;

      pointerRef.current.lastX = e.clientX;
      pointerRef.current.lastY = e.clientY;

      const dx = e.clientX - pointerRef.current.startX;
      const dy = e.clientY - pointerRef.current.startY;

      if (Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault?.();
      }

      pointerRef.current.moved = true;
    },
    [isApp]
  );

  const onPointerUp = useCallback(
    (e) => {
      if (!isApp) return;
      if (!pointerRef.current.down) return;
      if (pointerRef.current.pointerId !== e.pointerId) return;

      pointerRef.current.down = false;

      const dx = pointerRef.current.lastX - pointerRef.current.startX;
      const dy = pointerRef.current.lastY - pointerRef.current.startY;

      const THRESH = 45;
      const isMostlyHorizontal = Math.abs(dx) > Math.abs(dy);

      if (isMostlyHorizontal) {
        if (dx <= -THRESH) goNext();
        else if (dx >= THRESH) goPrev();
      }

      resetAutoTimer();
    },
    [isApp, goNext, goPrev, resetAutoTimer]
  );

  const onPointerCancel = useCallback(() => {
    pointerRef.current.down = false;
    pointerRef.current.pointerId = null;
  }, []);

  const appImages = useMemo(() => {
  if (!isApp) return gallery || [];

  if (appOverrideImg && !(gallery || []).includes(appOverrideImg)) {
    return [appOverrideImg, ...(gallery || [])];
  }

  return gallery || [];
}, [isApp, appOverrideImg, gallery]);

const appImg = useMemo(() => {
  if (!isApp) return "";
  return appImages?.[activeIndex] || appOverrideImg || "";
}, [isApp, appImages, activeIndex, appOverrideImg]);


  

  /* =========================
     COLOR BEHAVIOR
  ========================== */
  const applyColorSelection = useCallback(
    (colorObj) => {
      const label = colorObj?.label || "";
      const id = colorObj?.id || null;

      setSelectedColor(label);
      setSelectedColorId(id);

      const variants = Array.isArray(singleProduct?.colorVariants)
        ? singleProduct.colorVariants
        : [];

      const matchedVariant =
        id != null
          ? variants.find((v) => String(v?.color?._id || v?.color) === String(id))
          : null;

      const variantImgs = Array.isArray(matchedVariant?.images)
        ? matchedVariant.images
            .map((im) => (typeof im === "string" ? im : im?.url))
            .filter(Boolean)
        : [];

      const main =
        variantImgs[0] ||
        colorObj?.imageUrl ||
        colorObj?.allVariantImages?.[0] ||
        "";

      if (main) {
        setActiveImg(main);

        const idx = (gallery || []).findIndex((x) => String(x) === String(main));
        if (idx >= 0) {
            setActiveIndex(idx);
            setAppOverrideImg("");
            requestAnimationFrame(() => scrollToIndex(idx));
          } else {
            setAppOverrideImg(main);
            setActiveIndex(0);
            requestAnimationFrame(() => scrollToIndex(0));
          }

        resetAutoTimer();
      }
    },
    [singleProduct, gallery, resetAutoTimer, scrollToIndex]
  );

  /* =========================
     Variant requirements
  ========================== */
  const requiresSize = (normalized.sizes?.length || 0) > 0;
  const requiresColor = (normalized.colors?.length || 0) > 0;
  const isPrintable = Boolean(normalized.isPrintable);
  const requiresPrintSide = Boolean(printPricingEnabled);

  const [variantToastOpen, setVariantToastOpen] = useState(false);
  const [artworkUploads, setArtworkUploads] = useState([]);
  const [printInstructions, setPrintInstructions] = useState("");

  /* =========================
     ✅ ADD TO CART
  ========================== */
  const addToCartNow = useCallback(
    async ({
      sizeId,
      colorObj,
      artworkUploads: uploadsFromToast,
      printInstructions: instrFromToast,
      printSide: sideFromToast,
    } = {}) => {
      if (!normalized?.id) return;

      if (isInCart) {
        navigate("/cart");
        return;
      }

      const moq = Number(normalized?.minOrderQty || 1);
      const safeMOQ = Number.isFinite(moq) && moq >= 1 ? moq : 1;
      const finalQty = Math.max(safeMOQ, Math.max(1, Number(qty || 1)));

      const finalSizeId = sizeId ?? selectedSizeId;
      const sizeHit = finalSizeId
        ? (normalized.sizes || []).find((s) => String(s.id) === String(finalSizeId))
        : null;

      const finalSizeRaw = sizeHit?.raw || null;

      const finalColorLabel = colorObj?.label ?? selectedColor;
      const finalColorId = colorObj?.id ?? selectedColorId;

      const matchedVariantForCart =
        finalColorId != null && Array.isArray(singleProduct?.colorVariants)
          ? singleProduct.colorVariants.find(
              (v) => String(v?.color?._id || v?.color) === String(finalColorId)
            )
          : null;

      const variantImgForCart = Array.isArray(matchedVariantForCart?.images)
        ? matchedVariantForCart.images
            .map((im) => (typeof im === "string" ? im : im?.url))
            .filter(Boolean)?.[0]
        : "";

      const selectedVariantImg =
        variantImgForCart ||
        colorObj?.imageUrl ||
        colorObj?.allVariantImages?.[0] ||
        (isApp ? appOverrideImg || appImg || "" : activeImg) ||
        (normalized.images || [])[0] ||
        "";

      const uploads = Array.isArray(uploadsFromToast)
        ? uploadsFromToast
        : Array.isArray(artworkUploads)
        ? artworkUploads
        : [];

      const finalInstructions =
        typeof instrFromToast === "string" ? instrFromToast : printInstructions;

      const finalSide =
        typeof sideFromToast === "string" && sideFromToast ? sideFromToast : printSide;

      const safeUploads = uploads
        .filter(Boolean)
        .map((u) => ({
          fileName:
            u?.fileName ||
            u?.original_filename ||
            u?.originalFilename ||
            (u?.public_id ? String(u.public_id).split("/").pop() : "artwork"),
          public_id: u?.public_id,
          url: u?.url,
        }))
        .filter((f) => f.public_id || f.url);

      let unitPrice = finalPriceDisplay.current;
      let oldPrice = finalPriceDisplay.old || null;

      if (finalSizeRaw) {
        const sizePrice = getSizePriceByQtyDisplay(
          finalSizeRaw,
          finalQty,
          basePriceDisplay.current
        );

        if (sizePrice.hasOwnPrice || sizePrice.isBulkDiscount) {
          unitPrice = sizePrice.current;
          oldPrice = sizePrice.old ?? oldPrice;
        } else {
          const prodPrice = getProductPriceByQtyDisplay(
            finalQty,
            sizePrice.current,
            basePriceDisplay.old
          );
          unitPrice = prodPrice.current;
          oldPrice = prodPrice.old ?? oldPrice;
        }
      }

      const hasPrintPricing = Boolean(printPricingEnabled && finalSide);

      const printUnit = hasPrintPricing
        ? (() => {
            const base =
              Number(normalized?.printPricing?.printPrice?.[finalSide] ?? 0) || 0;
            const disc =
              Number(normalized?.printPricing?.printPriceDiscount?.[finalSide] ?? 0) || 0;

            const min = Number(normalized?.printPricing?.discountMinQty);
            const applies =
              Number.isFinite(min) && min >= 1 && Number(finalQty || 1) >= min;

            if (applies && Number.isFinite(disc) && disc > 0) return disc;
            return base;
          })()
        : 0;

      const unitFinal = Number(unitPrice || 0) + Number(printUnit || 0);

      const printKey = hasPrintPricing ? getPrintKey() : "";
      const prepareOnceRaw = hasPrintPricing
        ? Number(normalized?.printPricing?.preparePrice ?? 0) || 0
        : 0;

      const shouldApplyPrepare = Boolean(
        hasPrintPricing &&
          prepareOnceRaw > 0 &&
          printKey &&
          !hasPrepareAlreadyApplied(printKey)
      );

      const preparePriceOnce = shouldApplyPrepare ? prepareOnceRaw : 0;

      const payload = {
        productId: normalized.id,
        quantity: finalQty,
        price: Number(unitFinal || 0),
        variantImage: selectedVariantImg,
      };

      if (finalColorId) payload.color = finalColorId;
      if (finalSizeId) payload.size = finalSizeId;

      if (safeUploads.length) payload.uploadedFiles = safeUploads;
      if (finalInstructions && String(finalInstructions).trim()) {
        payload.instruction = String(finalInstructions).trim();
      }

      if (hasPrintPricing) {
        payload.printSide = finalSide;
        payload.printUnitPrice = Number(printUnit || 0);
        payload.printKey = printKey;
        payload.printPricingTitle = normalized?.printPricing?.title || "";
        payload.preparePriceOnce = Number(preparePriceOnce || 0);
        payload.preparePriceApplied = Boolean(preparePriceOnce > 0);
        payload.printDiscountMinQty = normalized?.printPricing?.discountMinQty ?? null;
      }

      const guestDisplay = {
        name: normalized.title,
        slug: normalized.slug || "",
        img: selectedVariantImg,
        colorLabel: finalColorLabel || "",
        sizeLabel: sizeHit?.nameWithMM || sizeHit?.name || "",
        oldPrice: oldPrice ?? null,
      };

      try {
        if (isLoggedIn) {
          await dispatch(addProdToCart(payload)).unwrap();

          if (window?.gtag) {
            window.gtag("event", "conversion", {
              send_to: "AW-17466736955/2LtTCM6W3ZUCeLuC5YhB",
            });
          }

          dispatch(fetchUserCart());
          showCartNotice("Product added successfully");
        } else {
          addToGuestCart({ ...payload, ...guestDisplay });

          if (window?.gtag) {
            window.gtag("event", "conversion", {
              send_to: "AW-17466736955/2LtTCM6W3ZUCeLuC5YhB",
            });
          }

          showCartNotice("Product added successfully");
        }
      } catch (err) {
        console.error("Add to cart failed:", err);
        showCartNotice("Failed to add product", "error");
      }
    },
    [
      dispatch,
      navigate,
      isInCart,
      isLoggedIn,
      addToGuestCart,
      normalized?.id,
      normalized?.slug,
      normalized?.title,
      normalized?.sizes,
      normalized?.minOrderQty,
      normalized?.images,
      normalized?.printPricing,
      printPricingEnabled,
      getPrintKey,
      hasPrepareAlreadyApplied,
      qty,
      selectedSizeId,
      selectedColor,
      selectedColorId,
      artworkUploads,
      printInstructions,
      printSide,
      finalPriceDisplay,
      basePriceDisplay,
      getSizePriceByQtyDisplay,
      getProductPriceByQtyDisplay,
      activeImg,
      isApp,
      appImg,
      appOverrideImg,
      singleProduct?.colorVariants,
      showCartNotice,
    ]
  );

  const handleAddToCartClick = useCallback(() => {
    if (guardOffline()) return;

    if (isInCart) {
      navigate("/cart");
      return;
    }

    const missingSize = requiresSize && !selectedSizeId;
    const missingColor = requiresColor && !selectedColorId;
    const missingPrintSide = requiresPrintSide && !printSide;

    if (missingSize || missingColor || isPrintable || missingPrintSide) {
      setVariantToastOpen(true);
      return;
    }

    addToCartNow();
  }, [
    guardOffline,
    isInCart,
    navigate,
    requiresSize,
    requiresColor,
    selectedSizeId,
    selectedColorId,
    isPrintable,
    requiresPrintSide,
    printSide,
    addToCartNow,
  ]);

  /* =========================
     WISHLIST
  ========================== */
  const onWishlist = useCallback(() => {
    if (guardOffline()) return;
    if (!normalized?.id) return;
    dispatch(addToWishlists(normalized.id));
  }, [dispatch, normalized.id, guardOffline]);

  /* =========================
     REVIEWS
  ========================== */
  const timeAgo = useCallback((date) => {
    if (!date) return "";
    const t = new Date(date).getTime();
    if (Number.isNaN(t)) return "";
    const diff = Date.now() - t;
    const sec = Math.max(1, Math.floor(diff / 1000));
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);
    const day = Math.floor(hr / 24);
    if (day > 0) return `${day} day${day === 1 ? "" : "s"} ago`;
    if (hr > 0) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
    if (min > 0) return `${min} minute${min === 1 ? "" : "s"} ago`;
    return "Just now";
  }, []);

  const getReviewerName = useCallback((postedBy) => {
    if (!postedBy) return "Customer";
    if (typeof postedBy === "object") {
      return (
        postedBy?.firstname ||
        postedBy?.firstName ||
        postedBy?.lastname ||
        postedBy?.lastName ||
        postedBy?.name ||
        postedBy?.fullName ||
        postedBy?.email ||
        "Customer"
      );
    }
    return "Customer";
  }, []);

  const buildTitleFromComment = useCallback((comment = "") => {
    const str = String(comment || "").trim();
    if (!str) return "Review";
    const first = str.split(".")[0].trim();
    const cut = (first || str).slice(0, 52);
    return cut.length < (first || str).length ? `${cut}...` : cut;
  }, []);

  const backendReviews = useMemo(() => {
    const arr = Array.isArray(normalized.ratingsArr) ? normalized.ratingsArr : [];

    return arr
      .map((r, idx) => {
        const star = Number(r?.star || 0);
        const comment = String(r?.comment || "").trim();
        const createdAt = r?.createdAt || r?.updatedAt || r?.date || null;
        const name = getReviewerName(r?.postedBy);

        return {
          id: r?._id || `r-${idx}`,
          name,
          verified: true,
          title: buildTitleFromComment(comment),
          text: comment || "—",
          rating: Math.min(5, Math.max(1, Number.isFinite(star) ? star : 0)) || 0,
          time: createdAt ? timeAgo(createdAt) : "",
        };
      })
      .filter((x) => x.rating > 0);
  }, [normalized.ratingsArr, getReviewerName, buildTitleFromComment, timeAgo]);

  const ratingStats = useMemo(() => {
    const list = backendReviews;
    const total = list.length;

    const avg =
      total > 0
        ? list.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / total
        : Number(normalized.avgRating || 0);

    const distCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    list.forEach((r) => {
      const s = Math.round(Number(r.rating) || 0);
      if (s >= 1 && s <= 5) distCounts[s] += 1;
    });

    const distPct = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    [1, 2, 3, 4, 5].forEach((n) => {
      distPct[n] = total ? Math.round((distCounts[n] / total) * 100) : 0;
    });

    return { avg: Number((avg || 0).toFixed(1)), total, dist: distPct };
  }, [backendReviews, normalized.avgRating]);

  const starFillCount = useMemo(() => Math.round(ratingStats.avg || 0), [ratingStats.avg]);

  const starsUI = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`sp-star ${i < starFillCount ? "filled" : ""}`}>
          ★
        </span>
      )),
    [starFillCount]
  );

  const ratingGraphRows = useMemo(() => {
    const dist = ratingStats?.dist || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    return [5, 4, 3, 2, 1].map((n) => ({ star: n, pct: Number(dist?.[n] ?? 0) }));
  }, [ratingStats?.dist]);

  /* =========================
     Tabs + Read more
  ========================== */
  const PREVIEW_LIMIT = 203;

  const plainDesc = useMemo(
    () => stripHtmlTags(normalized.description || ""),
    [normalized.description]
  );

  const showReadMore = useMemo(() => plainDesc.length > PREVIEW_LIMIT, [plainDesc]);

  const previewText = useMemo(
    () =>
      plainDesc.length <= PREVIEW_LIMIT
        ? plainDesc
        : plainDesc.slice(0, PREVIEW_LIMIT),
    [plainDesc]
  );

  const descTabRef = useRef(null);
  const [activeTab, setActiveTab] = useState("review");

  const onReadMore = useCallback(() => {
    setActiveTab("desc");
    requestAnimationFrame(() => {
      descTabRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      requestAnimationFrame(() =>
        descTabRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      );
    });
  }, []);

  const writeReviewRef = useRef(null);

  const scrollToWriteReview = useCallback(() => {
    setActiveTab("review");
    requestAnimationFrame(() =>
      writeReviewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  }, []);

  /* =========================
     RELATED PRODUCTS
  ========================== */
  const relatedProducts = useMemo(() => {
    const p = singleProduct || {};
    if (!p?._id || !Array.isArray(allProducts) || !allProducts.length) return [];

    const currentCatIds = (p.category || [])
      .map((c) => (typeof c === "object" ? c?._id : c))
      .filter(Boolean)
      .map(String);

    if (!currentCatIds.length) return [];

    return allProducts
      .filter((it) => it?._id && String(it._id) !== String(p._id))
      .filter((it) => {
        const itemCatIds = (it.category || [])
          .map((c) => (typeof c === "object" ? c?._id : c))
          .filter(Boolean)
          .map(String);
        return itemCatIds.some((id) => currentCatIds.includes(id));
      })
      .slice(0, 20);
  }, [singleProduct, allProducts]);

  /* =========================
     Rating submit (backend)
  ========================== */
  const [myRating, setMyRating] = useState(5);
  const [myText, setMyText] = useState("");


  useEffect(() => {
  const handleFocus = (e) => {
    const target = e.target;

    if (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.tagName === "SELECT"
    ) {
      setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 350);
    }
  };

  document.addEventListener("focusin", handleFocus);

  return () => {
    document.removeEventListener("focusin", handleFocus);
  };
}, []);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!normalized?.id) return;

    try {
      await dispatch(
        addRating({
          star: myRating,
          prodId: normalized.id,
          comment: myText,
        })
      ).unwrap();

      dispatch(getAProduct(productSlug));
      setMyRating(5);
      setMyText("");
    } catch (err) {
      console.error(err);
    }
  };

  /* =========================
     Guard states
  ========================== */
  const hasProduct = Boolean(normalized?.id);
  const hasImages = Boolean(isApp ? appImages?.length : gallery?.length);

  /* =========================
     ✅ SKELETON
  ========================== */
  const Skeleton = () => (
    <>
      <section className="sp-wrap">
        <div className="sp-shell">
          <div className="sp-left">
            <div className={`sp-mainImg ${isApp ? "sp-mainImg--app" : ""}`}>
              {isApp ? (
                <>
                  <div className="sp-appSlider sp-appSlider--skel" aria-hidden="true">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="sp-appSlide sp-appSlide--skel">
                        <div className="sk sk-appMedia" />
                      </div>
                    ))}
                  </div>

                  <div className="sp-appDots sp-appDots--skel" aria-hidden="true">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <span key={i} className={`sk sk-dot ${i === 0 ? "active" : ""}`} />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="sk sk-mainMedia" />
                </>
              )}
            </div>

            {!isApp ? (
              <div className="sp-thumbsRow">
                <div className="sp-thumbsTrack">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="sp-thumb sp-thumb--skel" aria-hidden="true">
                      <div className="sk sk-thumbMedia" />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          <div className="sp-right">
            <div className="sk sk-title" />
            <div className="sp-ratingRow">
              <div className="sk sk-stars" />
              <div className="sk sk-reviewMeta" />
            </div>

            <div className="sk sk-price" />

            <div className="sp-miniDesc">
              <div className="sk sk-line" />
              <div className="sk sk-line short" />
            </div>

            <div className="sp-divider" />

            <div className="sp-variants2">
              <div className="sp-varBlock">
                <div className="sk sk-varHead" />
                <div className="sp-sizeRow2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="sk sk-sizeBox" />
                  ))}
                </div>
              </div>

              <div className="sp-varBlock">
                <div className="sk sk-varHead" />
                <div className="sp-colorRow2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="sk sk-colorDot" />
                  ))}
                </div>
              </div>

              <div className="sp-varBlock">
                <div className="sk sk-varHead" />
                <div className="sp-sizeRow2">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="sk sk-sizeBox" />
                  ))}
                </div>
              </div>
            </div>

            <div className="sp-divider" />

            <div className="sp-buyRow">
              <div className="sk sk-qty" />
              {!isApp ? <div className="sk sk-addBtn" /> : null}
            </div>

            <div className="sp-actionsRow">
              <div className="sk sk-action" />
              <div className="sk sk-action" />
            </div>

            <div className="sp-divider" />

            <div className="sp-share">
              <div className="sk sk-shareLabel" />
              <div className="sp-shareIcons">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="sk sk-shareBtn" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sp-tabsSection">
        <div className="sp-tabsShell">
          <div className="sp-tabsTop">
            <div className="sk sk-tab" />
            <div className="sk sk-tab" />
            <div className="sk sk-tab" />
          </div>

          <div className="sp-tabPanel">
            <div className="sk sk-line" />
            <div className="sk sk-line" />
            <div className="sk sk-line short" />
            <div className="sk sk-line" />
          </div>
        </div>
      </section>
    </>
  );

if (isLoading && !hasProduct) return <Skeleton />;

if (isOffline || productFetchFailed) {
  return (
    <OfflineOverlay
      show
      message={
        isOffline
          ? "You are offline. Please check your internet connection."
          : "Something went wrong while loading this product. Please try again."
      }
      onRetry={() => {
        setProductFetchFailed(false);

        if (typeof window !== "undefined" && navigator.onLine) {
          setIsOffline(false);

          dispatch(getAProduct(productSlug))
            .unwrap()
            .catch((err) => {
              console.error("Retry product fetch failed:", err);
              setProductFetchFailed(true);
            });
        } else {
          setIsOffline(true);
          setProductFetchFailed(true);
        }
      }}
      onReload={() => window.location.reload()}
    />
  );
}

if (!isLoading && !hasProduct) {
  return (
    <section className="sp-wrap">
      <div className="sp-shell" style={{ padding: 30 }}>
        <div style={{ color: "#777" }}>Product not found.</div>

        <button
          type="button"
          className="sp-linkBtn"
          onClick={() => navigate(-1)}
          style={{ marginTop: 12 }}
        >
          Go back
        </button>
      </div>
    </section>
  );
}

  const recentList = (recentlyViewed || [])
    .filter(
      (x) =>
        String(x?.slug || "") !== String(normalized?.slug || "") &&
        String(x?.id || x?._id || "") !== String(normalized?.id || "")
    )
    .slice(0, 12);

  const printKeyForUI = printPricingEnabled ? getPrintKey() : "";

  const wouldApplyPrepareNow = Boolean(
    printPricingEnabled &&
      printSide &&
      printPreparePrice > 0 &&
      printKeyForUI &&
      !hasPrepareAlreadyApplied(printKeyForUI)
  );

  const estimatedLineTotal =
    Number(qty || 1) *
      (Number(finalPriceDisplay.current || 0) + Number(printUnitPrice || 0)) +
    (wouldApplyPrepareNow ? Number(printPreparePrice || 0) : 0);

  const printDiscountMinQtyUI =
    normalized?.printPricing?.discountMinQty != null
      ? Number(normalized.printPricing.discountMinQty)
      : null;

  const discountIsPossible = Boolean(
    printPricingEnabled && printSide && printDiscountMinQtyUI
  );

  const discountNote = discountIsPossible
    ? printDiscountApplies
      ? `Discount applied (Qty ≥ ${printDiscountMinQtyUI}).`
      : qty >= (printDiscountMinQtyUI || Infinity)
      ? `Discount should apply now (Qty ≥ ${printDiscountMinQtyUI}).`
      : `Add more to get discount (discount starts at Qty ≥ ${printDiscountMinQtyUI}).`
    : "";

  /* =========================
     ✅ SHARE (REAL LINKS)
  ========================== */
  const shareUrl = (() => {
    const abs =
      typeof window !== "undefined" && window.location?.origin
        ? `${window.location.origin}${location.pathname}`
        : "";
    return abs || "";
  })();

  const shareText = `${normalized.title} — ${formatUGX(
    Number(finalPriceDisplay.current || 0) + Number(printUnitPrice || 0)
  )}`;

  const openShare = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer,width=900,height=650");
  };

  const shareFacebook = () =>
    openShare(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    );

  const shareTwitter = () =>
    openShare(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareUrl
      )}&text=${encodeURIComponent(shareText)}`
    );

  const shareLinkedIn = () =>
    openShare(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareUrl
      )}`
    );

    const productJsonLd = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: normalized.title,
  description: plainDesc || normalized.title,
  image: normalized.images || [],
  brand: {
    "@type": "Brand",
    name:
      typeof normalized.brand === "object"
        ? normalized.brand?.title || normalized.brand?.name || "Kupto"
        : normalized.brand || "Kupto",
  },
  sku: normalized.id,
  offers: {
    "@type": "Offer",
    url: `https://www.kupto.co/product/${normalized.slug}`,
    priceCurrency: "UGX",
    price: String(Number(finalPriceDisplay.current || normalized.price || 0)),
    availability:
      Number(normalized.quantity || 0) > 0
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
  },
};

  return (
    <>

    <Helmet>
  <title>
    {normalized.title} | Kupto Uganda
  </title>

  <meta
    name="description"
    content={
      plainDesc?.slice(0, 160) ||
      `${normalized.title} available at Kupto Uganda`
    }
  />

  <link
    rel="canonical"
    href={`https://www.kupto.co/product/${normalized.slug}`}
  />

  <meta property="og:type" content="product" />

  <meta
    property="og:title"
    content={`${normalized.title} | Kupto Uganda`}
  />

  <meta
    property="og:description"
    content={
      plainDesc?.slice(0, 160) ||
      `${normalized.title} available at Kupto Uganda`
    }
  />

  <meta
    property="og:url"
    content={`https://www.kupto.co/product/${normalized.slug}`}
  />

  <meta
    property="og:image"
    content={normalized.images?.[0] || ""}
  />

  <script type="application/ld+json">
    {JSON.stringify(productJsonLd)}
  </script>
</Helmet>

      {cartNotice.open ? (
        <div
          className={`sp-cartNotice sp-cartNotice--${cartNotice.type}`}
          role="status"
          aria-live="polite"
        >
          <span className="sp-cartNoticeIcon">
            {cartNotice.type === "error" ? "!" : "✓"}
          </span>
          <span className="sp-cartNoticeText">{cartNotice.message}</span>

          <button
            type="button"
            className="sp-cartNoticeClose"
            onClick={() => setCartNotice((prev) => ({ ...prev, open: false }))}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      ) : null}

      {/* ✅ VARIANT MODAL */}
      <VariantToast
        open={variantToastOpen}
        onClose={() => setVariantToastOpen(false)}
        productTitle={normalized.title}
        sizes={(normalized.sizes || []).map((s) => s.nameWithMM || s.name)}
        colors={normalized.colors}
        initialSize={selectedSizeNameWithMM || ""}
        initialColorId={selectedColorId}
        requireSize={requiresSize}
        requireColor={requiresColor}
        isPrintable={isPrintable}
        printSpec={normalized.printSpec}
        requireArtwork={isPrintable}
        printPricing={normalized.printPricing}
        requirePrintSide={requiresPrintSide}
        initialPrintSide={printSide}
        qty={qty}
        prepareAlreadyApplied={Boolean(
          printKeyForUI && hasPrepareAlreadyApplied(printKeyForUI)
        )}
        onConfirm={({
          size,
          colorObj,
          artworkUploads,
          printInstructions,
          printSide: side,
        }) => {
          if (size) {
            const match =
              (normalized.sizes || []).find((x) => x.nameWithMM === size) ||
              (normalized.sizes || []).find((x) => x.name === size);
            if (match?.id) setSelectedSizeId(match.id);
          }

          if (colorObj) applyColorSelection(colorObj);

          setArtworkUploads(Array.isArray(artworkUploads) ? artworkUploads : []);
          setPrintInstructions(String(printInstructions || ""));
          if (side) setPrintSide(String(side));

          const match =
            (normalized.sizes || []).find((x) => x.nameWithMM === size) ||
            (normalized.sizes || []).find((x) => x.name === size);

          addToCartNow({
            sizeId: match?.id,
            colorObj,
            artworkUploads,
            printInstructions,
            printSide: side,
          });

          setVariantToastOpen(false);
        }}
      />

      {/* PRODUCT */}
      <section className="sp-wrap">
        <div className="sp-shell">
          {/* LEFT: GALLERY */}
          <div className="sp-left">
            <div
              className={`sp-mainImg ${isApp ? "sp-mainImg--app" : ""}`}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
            >
              {isApp ? (
                hasImages ? (
                  <div className="sp-appSlider" ref={appTrackRef} onScroll={onAppScroll}>
                    {(appImages || []).map((img, i) => (
                      <div
                        key={img + i}
                        className="sp-appSlide"
                        style={{ scrollSnapAlign: "start" }}
                      >
                        {ImgWithLoader({
                          src: img,
                          alt: `${normalized.title} ${i + 1}`,
                          className: "sp-mainImgEl",
                          draggable: false,
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="sp-emptyImg">No image</div>
                )
              ) : activeImg ? (
                ImgWithLoader({
                  src: activeImg,
                  alt: normalized.title,
                })
              ) : (
                <div className="sp-emptyImg">No image</div>
              )}

              {isApp && (appImages?.length || 0) > 1 ? (
                <div className="sp-appDots" aria-label="Image slider dots">
                  {appImages.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      className={`sp-appDot ${i === activeIndex ? "active" : ""}`}
                      onClick={() => {
                        setActiveIndex(i);
                        setAppOverrideImg("");
                        scrollToIndex(i);
                        resetAutoTimer();
                      }}
                      aria-label={`Go to image ${i + 1}`}
                      aria-pressed={i === activeIndex}
                    />
                  ))}
                </div>
              ) : null}
            </div>

            {!isApp && hasImages ? (
              <div className="sp-thumbsRow">
                <div className="sp-thumbsTrack">
                  {gallery.map((img, idx) => (
                    <button
                      type="button"
                      key={idx}
                      className={`sp-thumb ${activeImg === img ? "active" : ""}`}
                      onClick={() => setActiveImg(img)}
                    >
                      {ImgWithLoader({
                        src: img,
                        alt: `thumb-${idx + 1}`,
                        className: "sp-imgEl",
                        draggable: false,
                      })}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* RIGHT: DETAILS */}
          <div className="sp-right">
            <h1 className="sp-title">{normalized.title}</h1>

            <div className="sp-ratingRow">
              <div className="sp-stars">{starsUI}</div>
              <span className="sp-reviews">
                ({ratingStats.total} customer review{ratingStats.total === 1 ? "" : "s"})
              </span>
            </div>

            <div className="sp-price">
              <span>
                {formatUGX(
                  Number(finalPriceDisplay.current || 0) + Number(printUnitPrice || 0)
                )}
              </span>
              {finalPriceDisplay.old ? (
                <span className="sp-oldPrice">{formatUGX(finalPriceDisplay.old)}</span>
              ) : null}
            </div>

            {printPricingEnabled ? (
              <div className="sp-miniDesc" style={{ marginTop: 6 }}>
                <span className="sp-miniText">
                  <strong>Printing:</strong> {normalized?.printPricing?.title || "—"}{" "}
                  {printSide ? (
                    <>
                      • <strong>Side:</strong>{" "}
                      {printSide === "oneSide" ? "One-side" : "Two-side"}
                    </>
                  ) : null}
                  {printDiscountMinQtyUI ? (
                    <>
                      {" "}
                      • <strong>Discount from:</strong> Qty ≥ {printDiscountMinQtyUI}
                      {printDiscountApplies ? (
                        <span style={{ color: "#0a7" }}> (applied)</span>
                      ) : null}
                    </>
                  ) : null}
                  {printPreparePrice > 0 ? (
                    <>
                      {" "}
                      • <strong>Setup fee:</strong> {formatUGX(printPreparePrice)}{" "}
                      <span style={{ color: "#777" }}>
                        ({wouldApplyPrepareNow ? "charged once" : "already applied in cart"})
                      </span>
                    </>
                  ) : null}
                </span>

                {discountNote ? (
                  <div
                    style={{
                      marginTop: 6,
                      color: printDiscountApplies ? "#0a7" : "#777",
                      fontSize: 13,
                    }}
                  >
                    {discountNote}
                  </div>
                ) : null}
              </div>
            ) : null}

            {printPricingEnabled && printSide ? (
              <div className="sp-miniDesc" style={{ marginTop: 6 }}>
                <span className="sp-miniText">
                  <strong>Estimated total:</strong> {formatUGX(estimatedLineTotal)}
                </span>
              </div>
            ) : null}

            {plainDesc ? (
              <div className="sp-miniDesc">
                <span className="sp-miniText">
                  {previewText}
                  {showReadMore ? "..." : ""}
                </span>{" "}
                {showReadMore ? (
                  <button type="button" className="sp-readMoreBtn" onClick={onReadMore}>
                    Read More
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className="sp-divider" />

            <div className="sp-variants2">
              {requiresSize ? (
                <div className="sp-varBlock">
                  <div className="sp-varHead">
                    <span className="sp-varTitle">Size :</span>
                    <span className="sp-varValue">{selectedSizeNameWithMM || "-"}</span>
                  </div>

                  <div className="sp-sizeRow2" role="radiogroup" aria-label="Choose size">
                    {(normalized.sizes || []).map((s) => {
                      const isActive =
                        selectedSizeId && String(selectedSizeId) === String(s?.id);
                      return (
                        <button
                          key={s?.id || s?.name}
                          type="button"
                          className={`sp-sizeBox ${isActive ? "active" : ""}`}
                          onClick={() => setSelectedSizeId(s?.id || null)}
                          aria-pressed={isActive}
                          title={s?.nameWithMM || s?.name}
                        >
                          {s?.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {requiresColor ? (
                <div className="sp-varBlock">
                  <div className="sp-varHead">
                    <span className="sp-varTitle">Color :</span>
                    <span className="sp-varValue">{selectedColor || "-"}</span>
                  </div>

                  <div className="sp-colorRow2" role="radiogroup" aria-label="Choose color">
                    {(normalized.colors || []).map((c) => {
                      const label = c?.label || "";
                      const isActive =
                        selectedColorId && String(selectedColorId) === String(c?.id);
                      const hex = c?.hex || "#cccccc";

                      return (
                        <button
                          key={String(c?.id || label)}
                          type="button"
                          className={`sp-colorDot ${isActive ? "active" : ""}`}
                          style={{ background: hex }}
                          onClick={() => applyColorSelection(c)}
                          aria-label={label}
                          aria-pressed={isActive}
                          title={label}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {printPricingEnabled ? (
                <div className="sp-varBlock">
                  <div className="sp-varHead">
                    <span className="sp-varTitle">Print Side :</span>
                    <span className="sp-varValue">
                      {printSide ? (printSide === "oneSide" ? "One-side" : "Two-side") : "-"}
                    </span>
                  </div>

                  <div className="sp-sizeRow2" role="radiogroup" aria-label="Choose print side">
                    <button
                      type="button"
                      className={`sp-sizeBox ${printSide === "oneSide" ? "active" : ""}`}
                      onClick={() => setPrintSide("oneSide")}
                      aria-pressed={printSide === "oneSide"}
                      title="One-side printing"
                    >
                      One-side
                    </button>
                    <button
                      type="button"
                      className={`sp-sizeBox ${printSide === "twoSide" ? "active" : ""}`}
                      onClick={() => setPrintSide("twoSide")}
                      aria-pressed={printSide === "twoSide"}
                      title="Two-side printing"
                    >
                      Two-side
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="sp-divider" />

            {normalized.minOrderQty ? (
              <div className="sp-miniDesc" style={{ marginTop: 2 }}>
                <span className="sp-miniText">
                  <strong>Minimum order:</strong> {normalized.minOrderQty}
                </span>
              </div>
            ) : null}

            <div className="sp-miniDesc sp-brandCategory">
              <div className="sp-miniText">
                <strong>Brand:</strong> {normalized.brand || "—"}
              </div>

              <div className="sp-miniText">
                <strong>Category:</strong> {categoryText}
              </div>
            </div>

            <div className="sp-buyRow">
              <div className="sp-qty">
                <button
                  type="button"
                  className="sp-qtyBtn"
                  onClick={() => {
                    const next = Math.max(minOrderQty, Math.max(1, Number(qty || 1) - 1));
                    if (isInCart) {
                      syncQtyToExistingCart(next);
                    } else {
                      setQty(next);
                    }
                  }}
                  aria-label="Decrease quantity"
                >
                  −
                </button>

                <input
                  className="sp-qtyInput"
                  value={qty}
                  onChange={(e) => {
                    const v = parseInt(e.target.value || String(minOrderQty || 1), 10);
                    const safe = Number.isNaN(v) ? minOrderQty || 1 : v;
                    const next = Math.max(minOrderQty, Math.max(1, safe));

                    if (isInCart) {
                      syncQtyToExistingCart(next);
                    } else {
                      setQty(next);
                    }
                  }}
                  aria-label="Quantity"
                />

                <button
                  type="button"
                  className="sp-qtyBtn"
                  onClick={() => {
                    const next = Math.max(minOrderQty, Number(qty || 1) + 1);
                    if (isInCart) {
                      syncQtyToExistingCart(next);
                    } else {
                      setQty(next);
                    }
                  }}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>

              {!isApp ? (
                isInCart ? (
                  <button type="button" className="sp-addBtn" onClick={() => navigate("/cart")}>
                    <PiShoppingCartSimpleLight />
                    <span>GO TO CART</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="sp-addBtn"
                    onClick={handleAddToCartClick}
                    disabled={isLoading}
                  >
                    <PiShoppingCartSimpleLight />
                    <span>ADD TO CART</span>
                  </button>
                )
              ) : null}
            </div>

            <div className="sp-actionsRow">
              <button
                type="button"
                className="sp-linkBtn"
                onClick={onWishlist}
                disabled={isLoading}
              >
                <IoIosHeartEmpty />
                <span>Add to Wishlist</span>
              </button>

              <button type="button" className="sp-linkBtn" onClick={scrollToWriteReview}>
                <TfiWrite />
                <span>Write A Review</span>
              </button>
            </div>

            <div className="sp-divider" />

            <div className="sp-share">
              <span className="sp-shareLabel">Share this product</span>
              <div className="sp-shareIcons">
                <button
                  type="button"
                  className="sp-shareBtn"
                  aria-label="Facebook"
                  onClick={shareFacebook}
                >
                  <FaFacebookF />
                </button>
                <button
                  type="button"
                  className="sp-shareBtn"
                  aria-label="Twitter"
                  onClick={shareTwitter}
                >
                  <FaTwitter />
                </button>
                <button
                  type="button"
                  className="sp-shareBtn"
                  aria-label="LinkedIn"
                  onClick={shareLinkedIn}
                >
                  <FaLinkedinIn />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TABS */}
      <section className="sp-tabsSection">
        <div className="sp-tabsShell">
          <div className="sp-tabsTop" ref={descTabRef}>
            <button
              type="button"
              className={`sp-tabBtn ${activeTab === "desc" ? "active" : ""}`}
              onClick={() => setActiveTab("desc")}
            >
              Description
            </button>
            <button
              type="button"
              className={`sp-tabBtn ${activeTab === "info" ? "active" : ""}`}
              onClick={() => setActiveTab("info")}
            >
              Additional Information
            </button>
            <button
              type="button"
              className={`sp-tabBtn ${activeTab === "review" ? "active" : ""}`}
              onClick={() => setActiveTab("review")}
            >
              Review
            </button>
          </div>

          {activeTab === "desc" && (
            <div className="sp-tabPanel">
              <div className="sp-tabText">
                {normalized.description ? (
                  /<\/?[a-z][\s\S]*>/i.test(String(normalized.description)) ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: String(normalized.description),
                      }}
                    />
                  ) : (
                    <div>{String(normalized.description)}</div>
                  )
                ) : (
                  <div style={{ color: "#777" }}>—</div>
                )}
              </div>
            </div>
          )}

          {activeTab === "info" && (
            <div className="sp-tabPanel">
              <div className="sp-infoGrid">
                <div className="sp-infoRow">
                  <span className="sp-infoKey">Brand</span>
                  <span className="sp-infoVal">{normalized.brand || "—"}</span>
                </div>

                {(normalized.sizes?.length || 0) > 0 ? (
                  <div className="sp-infoRow">
                    <span className="sp-infoKey">Sizes</span>
                    <span className="sp-infoVal">
                      {Array.from(
                        new Set((normalized.sizes || []).map((s) => s.nameWithMM || s.name))
                      ).join(", ")}
                    </span>
                  </div>
                ) : null}

                {(normalized.colors?.length || 0) > 0 ? (
                  <div className="sp-infoRow">
                    <span className="sp-infoKey">Colors</span>
                    <span className="sp-infoVal">
                      {Array.from(new Set((normalized.colors || []).map((c) => c.label))).join(
                        ", "
                      )}
                    </span>
                  </div>
                ) : null}

                {printPricingEnabled ? (
                  <div className="sp-infoRow">
                    <span className="sp-infoKey">Print Type</span>
                    <span className="sp-infoVal">{normalized?.printPricing?.title || "—"}</span>
                  </div>
                ) : null}

                {normalized.minOrderQty ? (
                  <div className="sp-infoRow">
                    <span className="sp-infoKey">Minimum Order</span>
                    <span className="sp-infoVal">{normalized.minOrderQty}</span>
                  </div>
                ) : null}

                <div className="sp-infoRow">
                  <span className="sp-infoKey">Quantity</span>
                  <span className="sp-infoVal">
                    {selectedColorVariant?.quantity != null
                      ? Number(selectedColorVariant.quantity)
                      : Number.isFinite(normalized.quantity)
                      ? normalized.quantity
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "review" && (
            <div className="sp-tabPanel">
              <div className="sp-reviewSummary" style={{ marginBottom: 16 }}>
                <div className="sp-reviewSummaryGrid">
                  <div
                    className="sp-reviewAvg"
                    style={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 2,
                      padding: 14,
                    }}
                  >
                    <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>
                      {Number(ratingStats.avg || 0).toFixed(1)}
                    </div>
                    <div className="sp-stars" style={{ marginTop: 6 }}>
                      {starsUI}
                    </div>
                    <div style={{ color: "#666", marginTop: 6, fontSize: 14 }}>
                      {ratingStats.total} review{ratingStats.total === 1 ? "" : "s"}
                    </div>
                  </div>

                  <div
                    className="sp-reviewGraph"
                    style={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 2,
                      padding: 14,
                    }}
                  >
                    {ratingGraphRows.map((row) => (
                      <div
                        key={row.star}
                        className="sp-graphRow"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "54px 1fr 44px",
                          gap: 10,
                          alignItems: "center",
                          padding: "6px 0",
                        }}
                      >
                        <div style={{ fontSize: 13, color: "#444", whiteSpace: "nowrap" }}>
                          {row.star} ★
                        </div>

                        <div
                          className="sp-graphBarWrap"
                          style={{
                            height: 10,
                            borderRadius: 999,
                            background: "rgba(0,0,0,0.07)",
                            overflow: "hidden",
                          }}
                          aria-label={`${row.star} star rating bar`}
                        >
                          <div
                            className="sp-graphBarFill"
                            style={{
                              height: "100%",
                              width: `${Math.max(0, Math.min(100, row.pct))}%`,
                              borderRadius: 999,
                              background: "var(--color-e7cc67)",
                            }}
                          />
                        </div>

                        <div style={{ fontSize: 13, color: "#444", textAlign: "right" }}>
                          {row.pct}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="sp-writeWrap" ref={writeReviewRef}>
                <h3 className="sp-writeHeading">Write A Review</h3>

                <form className="sp-reviewForm" onSubmit={submitReview}>
                  <div className="sp-formGrid">
                    <div className="sp-formField">
                      <label className="sp-formLabel">Your Rating</label>

                      <div className="sp-pickStars" role="radiogroup" aria-label="Your rating">
                        {Array.from({ length: 5 }).map((_, i) => {
                          const val = i + 1;
                          const active = val <= myRating;
                          return (
                            <button
                              key={val}
                              type="button"
                              className={`sp-pickStar ${active ? "filled" : ""}`}
                              onClick={() => setMyRating(val)}
                              aria-pressed={active}
                            >
                              ★
                            </button>
                          );
                        })}
                        <span className="sp-pickValue">{myRating}.0</span>
                      </div>
                    </div>

                    <div className="sp-formField sp-formField--full">
                      <label className="sp-formLabel">Your Review</label>
                      <textarea
                        className="sp-formTextarea"
                        value={myText}
                        onChange={(e) => setMyText(e.target.value)}
                        placeholder="Write your experience..."
                        rows={5}
                        required
                      />
                    </div>
                  </div>

                  <div className="sp-formActions">
                    <button type="submit" className="sp-formSubmit" disabled={isLoading}>
                      Submit Review
                    </button>
                  </div>
                </form>
              </div>

              <div className="sp-reviewList" style={{ marginTop: 18 }}>
                {backendReviews.length ? (
                  backendReviews.map((r) => (
                    <article key={r.id} className="sp-reviewCard">
                      <div className="sp-reviewTop">
                        <div className="sp-reviewUser">
                          <div className="crm-avatar-big">🙂</div>
                          <div>
                            <div className="sp-reviewName">
                              {r.name}{" "}
                              {r.verified && <span className="sp-verified">(Verified)</span>}
                            </div>
                          </div>
                        </div>
                        <div className="sp-reviewTime">{r.time}</div>
                      </div>

                      <h4 className="sp-reviewHeading">{r.title}</h4>
                      <p className="sp-reviewText">{r.text}</p>
                    </article>
                  ))
                ) : (
                  <div style={{ color: "#777", padding: "10px 0" }}>No reviews yet.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* RELATED */}
      {relatedProducts.length ? (
        <section className="rv-section pb-30">
          <div className="hero-container">
            <div className="rv-head">
              <div className="rv-titleWrap">
                <h2 className="rv-title">Related Product</h2>
                <span className="rv-underline" />
              </div>

              <button
                type="button"
                className="rv-seeAllBtn"
                onClick={() => navigate("/product")}
                aria-label="See all related products"
              >
                See all ›
              </button>
            </div>

            <div className="rv-slider">
              <div className="rv-track">
                {relatedProducts.map((it) => {
                  const img =
                    (typeof it?.images?.[0] === "string"
                      ? it.images[0]
                      : it?.images?.[0]?.url) || "";

                  const current = Number(it?.price ?? 0);
                  const old =
                    it?.discountedPrice !== null &&
                    it?.discountedPrice !== undefined &&
                    Number(it?.discountedPrice) > current
                      ? Number(it.discountedPrice)
                      : null;

                  const targetSlug = it?.slug || "";

                  return (
                    <article
                      key={String(it._id)}
                      className="rv-card"
                      onClick={() => {
                        if (targetSlug) {
                          navigate(`/product/${targetSlug}`);
                        }
                      }}
                      style={{ cursor: targetSlug ? "pointer" : "default" }}
                    >
                      <div className="rv-imgWrap">
                        {img ? (
                          ImgWithLoader({
                            src: img,
                            alt: it.title,
                            className: "rv-imgMedia",
                            draggable: false,
                          })
                        ) : (
                          <div className="sp-emptyImg">No image</div>
                        )}
                      </div>

                      <div className="rv-body">
                        <div className="rv-titleRow">
                          <div className="rv-name" title={it.title}>
                            {it.title}
                          </div>
                        </div>

                        <div className="rv-prices">
                          <span className="rv-price">{formatUGX(current)}</span>
                          {old ? <span className="rv-old">{formatUGX(old)}</span> : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* RECENTLY VIEWED */}
      {recentList.length ? (
        <section className="rv-section pb-30">
          <div className="hero-container">
            <div className="rv-head">
              <div className="rv-titleWrap">
                <h2 className="rv-title">Recently Viewed</h2>
                <span className="rv-underline" />
              </div>

              <button
                type="button"
                className="rv-seeAllBtn"
                onClick={() => navigate("/product")}
                aria-label="See all recently viewed products"
              >
                See all ›
              </button>
            </div>

            <div className="rv-slider">
              <div className="rv-track" style={{ scrollSnapType: "x mandatory" }}>
                {recentList.map((x) => {
                  const targetSlug = x?.slug || "";
                  const fallbackId = x?.id || x?._id || "";

                  return (
                    <article
                      key={String(targetSlug || fallbackId)}
                      className="rv-card"
                      onClick={() => {
                        if (targetSlug) {
                          navigate(`/product/${targetSlug}`);
                        } else if (fallbackId) {
                          navigate(`/product/${fallbackId}`);
                        }
                      }}
                      style={{ cursor: "pointer", scrollSnapAlign: "start" }}
                    >
                      <div className="rv-imgWrap">
                        {x?.img ? (
                          ImgWithLoader({
                            src: x.img,
                            alt: x?.title || x?.name || "Product",
                            className: "rv-imgMedia",
                            draggable: false,
                          })
                        ) : (
                          <div className="sp-emptyImg">No image</div>
                        )}
                      </div>

                      <div className="rv-body">
                        <div className="rv-titleRow">
                          <div className="rv-name" title={x?.title || x?.name}>
                            {x?.title || x?.name || "Product"}
                          </div>
                        </div>

                        <div className="rv-prices">
                          <span className="rv-price">{formatUGX(Number(x?.price ?? 0))}</span>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <CartAddBar
        show={isApp}
        inCart={isInCart}
        disabled={isLoading}
        qty={qty}
        minQty={minOrderQty}
        onDecQty={() => {
          const next = Math.max(minOrderQty, Math.max(1, Number(qty || 1) - 1));
          if (isInCart) {
            syncQtyToExistingCart(next);
          } else {
            setQty(next);
          }
        }}
        onIncQty={() => {
          const next = Math.max(minOrderQty, Number(qty || 1) + 1);
          if (isInCart) {
            syncQtyToExistingCart(next);
          } else {
            setQty(next);
          }
        }}
        onAddToCart={handleAddToCartClick}
        onGoToCart={() => navigate("/cart")}
        primaryMode="go"
        onHome={() => navigate("/")}
        onCall={() => window.open("tel:+256704116349")}
      />
    </>
  );
};

export default SingleProduct;